import { useEffect, useState } from "react";

import { unwrapModel } from "@doitintl/cloudflow-commons";
import {
  type ApiServiceModelDescriptor,
  CloudflowEngine,
  type OperationPointer,
  type UnwrappedApiServiceModelDescriptor,
} from "@doitintl/cmp-models";
import { getCollection, type WithModel } from "@doitintl/models-any";
import { QueryClient } from "@tanstack/react-query";

import { getOperationByPointer } from "./useGetOperationById";
import { usePromiseStateSetter } from "./usePromiseStateSetter";

const modelQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  },
});

export async function getModelById(
  vendor: string,
  service: string,
  version: string,
  modelId: string
): Promise<WithModel<ApiServiceModelDescriptor>> {
  return modelQueryClient.fetchQuery({
    queryKey: ["cloudflow", "model", vendor, service, version, modelId],
    queryFn: async () => {
      const modelData = (
        await getCollection(CloudflowEngine)
          .doc("integrations")
          .collection("apis")
          .doc(vendor)
          .collection("services")
          .doc(service)
          .collection("versions")
          .doc(version)
          .collection("models")
          .doc(modelId)
          .get()
      ).asModelData();
      if (modelData === undefined) {
        throw new Error(`Unable to fetch model ${[vendor, service, version, modelId].join("/")}`);
      }
      return modelData;
    },
  });
}

export type ModelData = {
  model: WithModel<UnwrappedApiServiceModelDescriptor> | null;
  modelId: string | undefined;
  loading: boolean;
};

export function useUnwrappedApiActionModel(
  operationPointer: OperationPointer | null,
  modelId: string | undefined
): ModelData {
  const [modelData, setModelData] = useState<ModelData>({ model: null, modelId: undefined, loading: true });
  const setPromiseState = usePromiseStateSetter();

  useEffect(() => {
    if (!modelId || !operationPointer) {
      setModelData({
        model: null,
        modelId: undefined,
        loading: true,
      });
      return;
    }
    setModelData({ model: null, modelId, loading: true });
    const getReferencedModelById = (modelId: string) =>
      getModelById.bind(null, operationPointer.provider, operationPointer.service, operationPointer.version)(modelId);
    return setPromiseState(
      getReferencedModelById(modelId).then((model) => unwrapModel(getReferencedModelById, model)),
      (model) => {
        setModelData({ model, modelId, loading: false });
      }
    );
  }, [modelId, operationPointer, setPromiseState]);

  return modelData;
}

export const getOutputModelByOperationPointer = async (
  operationPointer: OperationPointer
): Promise<WithModel<UnwrappedApiServiceModelDescriptor> | null> => {
  const operationData = await getOperationByPointer(operationPointer);

  if (!operationData.outputModel) {
    return null;
  }

  const model = await getModelById(
    operationPointer.provider,
    operationPointer.service,
    operationPointer.version,
    operationData.outputModel
  );

  return unwrapModel(
    async (modelId: string) =>
      getModelById(operationPointer.provider, operationPointer.service, operationPointer.version, modelId),
    model
  );
};
